import { Button, Div, H1, Modal, P, Skeleton } from '@dnb/eufemia';
import type { JSX, PropsWithChildren, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import useReturnTo from '@/hooks/useReturnTo';

import Container from '../Container';

import style from './index.module.css';

type ModalPageProps = PropsWithChildren<{
  title?: ReactNode;
  subtitle?: ReactNode;
  description?: ReactNode;
  actualModal?: boolean;
  skeleton?: boolean;
  onClose?: () => void;
  hideCloseButton?: boolean;
}>;

function FullScreenPage({
  title,
  subtitle,
  description,
  skeleton,
  onClose,
  children,
  hideCloseButton = false,
}: ModalPageProps): JSX.Element {
  const navigate = useNavigate();
  const [returnTo, , clearReturnTo] = useReturnTo();

  const _onClose = () => {
    if (onClose) {
      return onClose();
    }
    navigate(returnTo ?? '/');
    clearReturnTo();
  };

  return (
    <Div className={style['ModalPage']}>
      <Skeleton show={skeleton}>
        {(title || subtitle || !hideCloseButton) && (
          <div className={style['ModalPageHeaderContainer']}>
            <Container centered>
              <div className={style['ModalPageHeaderContent']}>
                <header className={style['ModalPageHeaderContentLeft']}>
                  {title && <H1 size="x-large">{title}</H1>}
                  {subtitle && <P>{subtitle}</P>}
                </header>
                {!hideCloseButton && (
                  <div className={style['ModalPageHeaderContentRight']}>
                    <Button
                      icon="Close"
                      iconPosition="left"
                      left="large"
                      onClick={_onClose}
                      size="large"
                      variant="tertiary"
                    >
                      Close
                    </Button>
                  </div>
                )}
              </div>
            </Container>
          </div>
        )}
        <Container bottom="large" centered top="large">
          {description && <P bottom="large">{description}</P>}
          {children}
        </Container>
      </Skeleton>
    </Div>
  );
}

export default function ModalPage({ actualModal = false, ...props }: ModalPageProps): JSX.Element {
  if (actualModal) {
    return (
      <Modal
        fullscreen
        hideCloseButton
        noAnimation
        open
        preventClose
        triggerAttributes={{
          hidden: true,
        }}
      >
        <Div className={style['Wrapper']}>
          <FullScreenPage {...props} />
        </Div>
      </Modal>
    );
  }
  return <FullScreenPage {...props} />;
}
